import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../zodios/client";
import { errorToast, successToast } from "../../../utils/toasts";
import { type z } from "zod";
import { type bulkMutateUserRolesRequestSchema } from "../../../zodios/apiDefinitions/api/userManagement";
import { isAxiosError } from "axios";

type Payload = z.infer<typeof bulkMutateUserRolesRequestSchema>;

export const useBulkMutateUserRolesMutation = () => {
  const { t } = useTranslation(["user_management"]);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: Payload) => {
      return await apiClient.bulkMutateUserRoles(vars);
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.user_management["User permissions successfully updated."]),
      });
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const stringifiedQueryKey = JSON.stringify(query.queryKey);
          return (
            stringifiedQueryKey.toLowerCase().includes("permissions") ||
            stringifiedQueryKey.toLowerCase().includes("users") ||
            stringifiedQueryKey.toLowerCase().includes("manageduser")
          );
        },
      });
    },
    onError: (error) => {
      let errorMessage = t(($) => $.user_management["Failed to update user permissions"]);

      if (isAxiosError(error)) {
        errorMessage = error.response?.data?.error ?? errorMessage;
      }

      errorToast({ message: errorMessage, error });
    },
  });
};
